import React, { useState, useCallback } from "react"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip
} from "@mui/material"
import styled from "@emotion/styled"
import { useDispatch, useSelector } from "react-redux"
import InfoIcon from "@mui/icons-material/Info"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"

import styles from "./ExpandableTable.module.scss"
import avatar from "assets/images/user/avatarUser.svg"
import Fire from "assets/icons/fire.svg"
import expand from "assets/icons/expand.svg"
import minimize from "assets/icons/minimize.svg"
import TableNoRecords from "components/TableNoRecords"
import AppPagination from "components/StyledComponents/AppPagination"
import { appActions } from "redux/app/slice/app"
import ProfileDisplayer from "components/ProfileDisplayer"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid ${theme.palette.background.default}`
  },
  "& .MuiTableCell-head": {
    position: "relative",
    padding: "24px 24px 12px 0px",
    "&:last-child": { paddingRight: 0 }
  }
}))

const ExpandableTable = ({
  columns,
  rowsData = [],
  setPage,
  currentPage,
  totalPage,
  expandMapping = {},
  isGroupLeaderboard
}) => {
  const dispatch = useDispatch()
  const [sort, setSort] = useState("")
  const [sortDirection, setSortDirection] = useState("")
  const [expandedGroups, setExpandedGroups] = useState({})
  const { isLoading } = useSelector((state) => state?.app)

  const toggleGroupExpansion = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  const getVisibleColumns = () => {
    const result = []

    for (const column of columns) {
      if (!column.isHidden || column.expandAction) {
        result.push(column)

        if (column.expandAction && expandedGroups[column.id]) {
          const hiddenColumns = expandMapping[column.id] || []
          const expandedCols = columns.filter((col) => hiddenColumns.includes(col.id))
          result.push(...expandedCols)
        }
      }
    }

    return result
  }

  const renderCellContent = (column, row, isTopRank, index) => {
    switch (column.id) {
      case "rank":
        return (
          <span className={`${isTopRank ? styles.rank : ""} ${styles.rankPadding}`}>
            {isGroupLeaderboard ? row?.rank : (currentPage - 1) * 12 + index + 1}
          </span>
        )
      case "name":
        return (
          <div className={styles.tableAvatar}>
            {row?.image_url ? (
              <ProfileDisplayer image={row?.image_url} />
            ) : (
              <div className={styles.tableAvatar__img}>
                <img src={avatar} />
              </div>
            )}
            <div className="d-flex flex-row align-items-center gap-3">
              <Typography variant="body2Regular">{row.name}</Typography>
              {(isGroupLeaderboard
                ? [1, 2, 3].includes(row.rank)
                : [1, 2, 3].includes(row?.national_rank)) && (
                <img width={12} height={12} src={Fire} alt="Top rank" className={styles.fireIcon} />
              )}
            </div>
          </div>
        )
      default:
        return row[column.id] ?? ""
    }
  }

  const handleSortDirection = useCallback((dir, id) => {
    setSortDirection(dir)
    handleSort(dir, id)
  }, [])

  const handleSort = useCallback(
    (dir, id) => {
      const order = dir === "asc" ? id : `-${id}`
      setSort(order)
      dispatch(appActions.setOrderBy(order))
    },
    [dispatch]
  )

  return (
    <>
      <Paper className={styles.tableContainer}>
        <TableContainer>
          <CustomTable stickyHeader aria-label="leaderboard table">
            <TableHead>
              <TableRow>
                {getVisibleColumns()?.map(
                  ({ id, align, minWidth, expandAction, tooltip, isSorting, label, sortId }) => (
                    <TableCell key={id} align={align} style={{ minWidth }}>
                      <Typography variant="body2Regular" className={styles.headerCell}>
                        {expandAction &&
                          (expandedGroups[id] ? (
                            <img
                              src={minimize}
                              onClick={() => toggleGroupExpansion(id)}
                              className={styles.cursorPointer}
                              alt="Collapse"
                            />
                          ) : (
                            <img
                              src={expand}
                              onClick={() => toggleGroupExpansion(id)}
                              className={styles.cursorPointer}
                              alt="Expand"
                            />
                          ))}
                        {label}
                        {tooltip && (
                          <Tooltip title={tooltip}>
                            <InfoIcon fontSize="small" />
                          </Tooltip>
                        )}
                        {isSorting && (
                          <span className={styles.cursorPointer}>
                            {sortDirection === "desc" && sort === `-${sortId}` ? (
                              <ArrowDownwardIcon
                                onClick={() => handleSortDirection("asc", sortId)}
                                fontSize="small"
                              />
                            ) : (
                              <ArrowUpwardIcon
                                onClick={() => handleSortDirection("desc", sortId)}
                                fontSize="small"
                              />
                            )}
                          </span>
                        )}
                      </Typography>
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsData?.length === 0 ? (
                <TableNoRecords
                  colSpan={columns.length}
                  label={
                    isLoading
                      ? "Please wait, we are fetching the leaderboard data..."
                      : "No Data Found"
                  }
                />
              ) : (
                rowsData.map((row, index) => {
                  const isTopRank = isGroupLeaderboard
                    ? [1, 2, 3].includes(row?.rank)
                    : [1, 2, 3].includes(row?.national_rank)

                  return (
                    <TableRow key={row.id + index.toString()}>
                      {getVisibleColumns()?.map((column) => (
                        <TableCell key={column.id} align={column.align || "left"}>
                          {renderCellContent(column, row, isTopRank, index)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
      </Paper>
      {rowsData.length > 0 && (
        <AppPagination onPageChange={setPage} totalPages={totalPage} currentPage={currentPage} />
      )}
    </>
  )
}

export default ExpandableTable
